import { RECEIPT_PDF_LOGO_BOX, fitInto } from './receiptLogoBox';

/** The logo as a PDF image source, in colour. */
export type ReceiptLogoImage = {
  dataUri: string;
  width: number;
  height: number;
};

const LOG = '[receipt-logo-image]';

const memory = new Map<string, ReceiptLogoImage>();

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img');
    // Cloudinary allows cross-origin reads; without this the canvas is tainted.
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('could not load the logo'));
    img.src = url;
  });
}

/**
 * Fit the logo into twice the box (crisp when the PDF is zoomed) and hand it
 * over as a PNG, which keeps colour and transparency for jsPDF.
 */
function toPng(img: HTMLImageElement): ReceiptLogoImage {
  const { width, height } = fitInto(
    img.naturalWidth,
    img.naturalHeight,
    RECEIPT_PDF_LOGO_BOX.width * 2,
    RECEIPT_PDF_LOGO_BOX.height * 2,
  );
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas unavailable');
  ctx.drawImage(img, 0, 0, width, height);
  return { dataUri: canvas.toDataURL('image/png'), width, height };
}

/**
 * The logo for the shared PDF, or null when it can't be had; never throws,
 * so a missing logo never stops a share.
 */
export async function loadReceiptLogoImage(
  url: string,
): Promise<ReceiptLogoImage | null> {
  const cached = memory.get(url);
  if (cached) return cached;
  try {
    const image = toPng(await loadImage(url));
    memory.set(url, image);
    return image;
  } catch (err) {
    console.log(
      LOG,
      'sharing without logo:',
      err instanceof Error ? err.message : String(err),
    );
    return null;
  }
}
